import { CheckIcon } from '@primer/octicons-react';
import { graphql, useFragment } from 'react-relay';

import { BookBookScrapsClassificationDialog_book$key } from '../../../relay/__generated__/BookBookScrapsClassificationDialog_book.graphql';
import { BookBookScrapsClassificationDialog_bookScrapsClassificationMutation } from '../../../relay/__generated__/BookBookScrapsClassificationDialog_bookScrapsClassificationMutation.graphql';
import Button from '../../core/Button';
import CheckboxField from '../../core/CheckboxField';
import Dialog, { DialogProps } from '../../core/Dialog';
import FormLayout from '../../core/FormLayout';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import Text from '../../core/Text';

const BookBookScrapsClassificationDialog_book = graphql`
  fragment BookBookScrapsClassificationDialog_book on Book {
    id
    unclassifiedBookScrapCount
  }
`;

type Props = {
  book: BookBookScrapsClassificationDialog_book$key;
  /** 고른 문항. 있으면 단원·임베딩이 있어도 이 문항만 다시 분류한다. */
  bookScraps?: readonly string[];
} & DialogProps &
  Pick<
    MutationFormikProps<BookBookScrapsClassificationDialog_bookScrapsClassificationMutation>,
    'onSubmit' | 'config' | 'connections'
  >;

const BookBookScrapsClassificationDialog = ({
  book,
  bookScraps = [],
  onSubmit,
  config,
  connections,
  ...props
}: Props) => {
  const { id, unclassifiedBookScrapCount } = useFragment(BookBookScrapsClassificationDialog_book, book);
  const isSelected = bookScraps.length > 0;

  return (
    <Dialog {...props}>
      <Dialog.Header>
        <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>단원 일괄 재분류</Text>
      </Dialog.Header>
      <MutationFormik<BookBookScrapsClassificationDialog_bookScrapsClassificationMutation>
        mutation={graphql`
          mutation BookBookScrapsClassificationDialog_bookScrapsClassificationMutation(
            $input: BookScrapsClassificationInput!
          ) {
            bookScrapsClassification(input: $input) {
              id
              unclassifiedBookScrapCount
              ...BookActionsMutationButtonStack_book
            }
          }
        `}
        initialValues={{ book: id, refillTex: false, bookScraps: isSelected ? bookScraps : null }}
        onSubmit={onSubmit}
        config={{
          ...config,
          // 서버가 고른 문항의 단원·난이도를 비우고 시작하므로, 목록에도 바로 ? 로 보이게 맞춘다.
          updater: (store, data) => {
            config?.updater?.(store, data);
            bookScraps.forEach((bookScrapId) => {
              const record = store.get(bookScrapId);
              record?.setValue(null, 'unitD');
              record?.setValue(null, 'difficultyLevel');
            });
          },
        }}
        connections={connections}
      >
        {({ handleSubmit }, { scrollContainerRef, nonFieldErrorRef }) => (
          <>
            <Dialog.Body ref={scrollContainerRef}>
              <FormLayout>
                <NonFieldError ref={nonFieldErrorRef} />
                <Text sx={{ fontSize: 1 }}>
                  {isSelected
                    ? `선택한 문항 ${bookScraps.length}건의 단원·난이도를 비우고 다시 분류해요. 한 건에 몇 초씩 걸려서 백그라운드로 돌아가고, 끝나는 대로 ? 가 사라져요.`
                    : `단원이 비어 있는 문항 ${unclassifiedBookScrapCount}건을 다시 분류해요. 한 건에 몇 초씩 걸려서 백그라운드로 돌아가고, 끝나는 대로 ? 가 사라져요.`}
                </Text>
                <CheckboxField
                  label={'tex도 다시 뽑기'}
                  name={'refillTex'}
                  caption={'OCR이 넣은 tex가 깨졌을 때만 켜주세요. 이미지부터 다시 읽어서 더 오래 걸려요.'}
                />
              </FormLayout>
            </Dialog.Body>
            <Dialog.Footer>
              <Button
                leadingIcon={CheckIcon}
                variant={'primary'}
                disabled={!isSelected && unclassifiedBookScrapCount === 0}
                onClick={() => handleSubmit()}
              >
                재분류 시작하기
              </Button>
            </Dialog.Footer>
          </>
        )}
      </MutationFormik>
    </Dialog>
  );
};

export default BookBookScrapsClassificationDialog;
